import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProductBasicInfo from '../../../components/ProductManagement/ProductBasicInfo';
import ProductImagesEdit from '../../../components/ProductManagement/ProductImagesEdit';
import ProductDescription from '../../../components/ProductManagement/ProductDescription';
import ProductPreview from '../../../components/ProductManagement/ProductPreview';
import { productApi, categoryApi } from '../../../services/productApi';
import {productReviewAdminApi} from '../../../services/productReviewAdminApi';
import Heading2 from "../../../components/Basic/Heading2";
import ToggleField from "../../../components/ProductManagement/ToggleField"
import Button from "../../../components/Button";
import api from '../../../api';
import {toast} from "react-hot-toast";

const ProductEdit = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const [imageItems, setImageItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [productData, setProductData] = useState({
    name: '',
    brand: '',
    category: null,
    description: '',
    unit_of_measure: "count",
    status:'',
    is_active: true,
    attributes: {},
    custom_attributes: [],
    is_packable: true,
    is_fragile: false,
    is_flammable: false,
    is_liquid: false,
    is_corrosive: false,
    width:0,
    length:0,
    height:0,
    weight:0,
    delivery_by_supplier: true,
    delivery_by_customer: true,
    delivery_by_carrier: true,
    images: [],
    featured_image: null,
  });
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [reviewRes, setReviewRes] = useState({
    comment:'',
    reviews_count:0,
  })


  // ---------- بارگذاری محصول ----------
const loadProduct = async () => {
    if (!id) return;

    try {
      setLoading(true);

      // 1. بارگذاری محصول
      const productResponse = await productApi.get(id);
      const product = productResponse.data;
      try {
        const reviewResponse = await productReviewAdminApi.getLastReview(id);

        // 🔥 اصلاح CRITICAL: چون getLastReview خودِ response.data را return می‌کند،
        // نیاز به دسترسی مجدد به فیلد data نیست. در صورت وجود ساختار دیگر، هردو حالت هندل می‌شود.
        const revRes = reviewResponse?.data !== undefined ? reviewResponse.data : reviewResponse;

        if (revRes) {
          const formatedReviewRes = {
            comment: revRes.comment || '',
            reviews_count: revRes.reviews_count || 0,
          };
          setReviewRes(formatedReviewRes);
        }
        } catch (reviewErr) {
          console.warn("⚠️ اطلاعات بازرسی قبلی یافت نشد یا در دسترس نیست:", reviewErr);
      }

      // 2. بارگذاری دسته‌بندی‌ها
      setLoadingCategories(true);
      const categoriesResponse = await categoryApi.getLeaves();
      setCategories(categoriesResponse.data || []);
      setLoadingCategories(false);

      // 3. 🔥 **اصلاح CRITICAL: تبدیل صحیح مشخصه‌ها**
      // 3.1 مشخصه‌های سیستمی - با ساختار کاملاً صحیح
      const systemAttributes = {};

      if (product.attribute_values && Array.isArray(product.attribute_values)) {
        product.attribute_values.forEach(attr => {
          // **مهم: استفاده از attr.id (که attribute_id هست)**
          const attributeId = attr.attribute_id;

          if (attributeId) {
            systemAttributes[attributeId] = {
              value: attr.value ?? "",
              is_featured: attr.is_featured || false
            };
          }
        });
      }

      // 3.2 مشخصه‌های سفارشی
      const customAttributes = (product.custom_attributes || []).map(attr => ({
        id: attr.id, // برای آیتم‌های موجود
        uuid: attr.uuid || crypto.randomUUID(),
        title: attr.title || "",
        value: attr.value ?? "",
        unit: attr.unit || "",
        is_featured: attr.is_featured || false
      }));

      const formattedProduct = {
        ...product,
        attributes: systemAttributes, // **OBJECT مهم!**
        custom_attributes: customAttributes,
        images: product.images || [],
        featured_image: product.featured_image || null,
      };
      setProductData(formattedProduct);

      // 4. 🔥 **بارگذاری مشخصه‌های دسته با دیباگ**
      if (formattedProduct.category) {
        try {

          const attrResponse = await categoryApi.getAttributes(formattedProduct.category);
          setCategoryAttributes(attrResponse.data || []);
        } catch (error) {
          console.error("❌ خطا در بارگذاری مشخصه‌های دسته:", error);
        }
      } else {
        console.warn("⚠️ محصول category ندارد!");
      }

    } catch (err) {
      console.error("❌ خطا در بارگذاری محصول:", err);
      alert("❌ خطا در بارگذاری محصول");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  loadProduct();
}, [id, navigate]);

const prepareProductData = () => {

  const imagesMetadata = imageItems.map((img, index) => ({
    id: img.id,
    order: index,
    is_featured: Boolean(img.is_featured),
  }));

  return {
    id: productData.id,
    name: productData.name || "",
    brand: productData.brand || "",
    category: productData.category,
    description: productData.description || "",
    unit_of_measure: productData.unit_of_measure,
    status: productData.status,
    is_active: productData.is_active,

    attributes: productData.attributes || {},
    custom_attributes: productData.custom_attributes || [],

    is_packable: productData.is_packable,
    is_fragile: productData.is_fragile,
    is_flammable: productData.is_flammable,
    is_liquid: productData.is_liquid,
    is_corrosive: productData.is_corrosive,

    width: productData.width,
    length: productData.length,
    height: productData.height,
    weight: productData.weight,

    delivery_by_supplier: productData.delivery_by_supplier,
    delivery_by_customer: productData.delivery_by_customer,
    delivery_by_carrier: productData.delivery_by_carrier,

    images_metadata: JSON.stringify(imagesMetadata)
  };
};

const buildFormData = (submitData) => {

  const formData = new FormData();

  Object.entries(submitData).forEach(([key, value]) => {

    if (
      value === undefined ||
      value === null
    ) return;

    if (key === "attributes") {
      formData.append(key, JSON.stringify(value));
    }

    else if (key === "custom_attributes") {
      formData.append(key, JSON.stringify(value));
    }

    else {
      formData.append(key, value);
    }
  });

  imageItems
    .filter(img => img.status === "new")
    .forEach(img => {
      formData.append("uploaded_images", img.file);
    });

  return formData;
};

const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const submitData = prepareProductData();
        submitData.status = "draft";

        const formData = buildFormData(submitData);

        const response = await productApi.update(
            submitData.id,
            formData
        );

        toast.success("کالا به‌عنوان پیش‌نویس ذخیره شد.")
        navigate(-1);
    } catch (err) {
      console.error('خطا در ذخیره پیش‌نویس:', err);
      alert('خطا در ذخیره پیش‌نویس: ' + JSON.stringify(err.response?.data || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!productData.name.trim()) {
        toast.error('نام کالا باید وارد شود.')
        return;
    }
    if (!productData.brand.trim()) {
      toast.error('برند کالا باید وارد شود.')
      return;
    }
    if (!productData.category) {
      toast.error('دسته کالا باید انتخاب شود.')
      return;
    }

    if (imageItems.length === 0) {
      toast.error('حداقل یک عکس باید انتخاب شود.')
      return;
    }
    const hasFeaturedImage = imageItems.some((item) => item.is_featured === true);

    if (!hasFeaturedImage) {
      toast.error('عکس شاخص باید انتخاب شود.');
      return;
    }
    setSaving(true);
    try {
      const submitData = prepareProductData();

        submitData.status = "pending";

        const formData = buildFormData(submitData);

        const response = await productApi.update(
            submitData.id,
            formData
        );

      toast.success("کالا برای تایید ارسال شد.")

      setProductData(()=>({
        name: '',
        category: null,
        description: '',
        brand: '',
        unit_of_measure: "count",
        status:'',
        is_active:true,
        attributes: {},
        custom_attributes: [],
        is_packable: true,
        is_fragile: false,
        is_flammable: false,
        is_liquid: false,
        is_corrosive: false,
        width:0,
        length:0,
        height:0,
        weight:0,
        delivery_by_supplier: true,
        delivery_by_customer: true,
        delivery_by_carrier: true,
        images: [],
        featured_image: null,
      }));
      navigate(-1);
    } catch (err) {
      console.error('خطا در ارسال محصول:', err);
      alert('خطا در ارسال محصول: ' + JSON.stringify(err.response?.data || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleChange = (fieldKey, value) => {
    setProductData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleDeleteProduct = async () => {
    const confirmed = window.confirm(
      'آیا از حذف این کالا مطمئن هستید؟\n\nاگر کالا سفارشی نداشته باشد کامل حذف می‌شود؛ در غیر این صورت فقط غیرفعال و رد خواهد شد.'
    );

    if (!confirmed) return;

    try {
      setSaving(true);
      const response = await productApi.delete(id);
      const message = response?.data?.message || 'عملیات با موفقیت انجام شد.';
      toast.success("کالا با موفقیت حذف شد.")
      navigate(-1);
    } catch (err) {
      console.error('خطا در حذف کالا:', err);
      alert('خطا در حذف کالا: ' + JSON.stringify(err.response?.data || err.message));
    } finally {
      setSaving(false);
    }
  };


   // برای نمایش در پیش‌نمایش
  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'بدون دسته‌بندی';
    const found = categories.find(c => c.id === categoryId);
    return found ? found.name : `دسته‌بندی ${categoryId}`;
  };

  const getPreviewData = () => {
    return {
      ...productData,
      category: productData.category ? {
        id: productData.category,
        name: getCategoryName(productData.category)
      } : null,
      width: productData.width || 0,
      length: productData.length || 0,
      height: productData.height || 0,
      weight: productData.weight || 0,
      images: imageItems || [],
      attributes: productData.attributes || {},
      custom_attributes: productData.custom_attributes || [],
      description: productData.description || ''
    };
  };

    // ---------- انصراف ----------
  const handleCancel = () => {
    if (window.confirm('آیا از انصراف مطمئن هستید؟ تغییرات ذخیره نخواهند شد.')) {
      navigate(-1);
    }
  };
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">در حال بارگذاری محصول...</p>
        </div>
      </div>
    );
  };

    const PreviewModal = ({ open, onClose }) => {
    if (!open) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black bg-opacity-50 overflow-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl my-4">
          <div className="flex justify-between items-center p-6 border-b">
            <h3 className="text-xl font-bold text-gray-800">پیش‌نمایش محصول</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          <div className="p-6 max-h-[80vh] overflow-y-auto">
            <ProductPreview
                productData={getPreviewData()}
                categoryAttributes={categoryAttributes}/>
          </div>

          <div className="flex justify-end p-6 border-t">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              بستن
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
      <div className="p-6">
        {/* هدر */}
        <div className="mb-6 pb-4 border-b">
          <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            بازگشت
          </button>
          <h1 className="text-2xl font-bold text-gray-800 my-2"> ویرایش محصول: <span
              className='text-xl font-semibold'> {productData.name}</span></h1>

          {reviewRes.comment && (
              <div>
                <label className="text-gray-600 my-[16px]">
                  نظرات کارشناس محتوی کالا:
                </label>
                <div className="bg-gray-50 border rounded-[4px] p-[8px] text-red-500 font-[18]">
                  {reviewRes.comment}
                </div>
                <p className='my-[8px] text-[16px]'>تعداد مرتبه بازرسی: {reviewRes.reviews_count}</p>
              </div>
          )}
        </div>

        {/* ---------- اطلاعات پایه ---------- */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6 border">
          <Heading2 text={'اطلاعات کلی کالا'}/>
          <ProductBasicInfo
              productData={productData}
              setProductData={setProductData}
              categoryAttributes={categoryAttributes}
              setCategoryAttributes={setCategoryAttributes}
          />
        </section>

        {/* ---------- روش‌های ارسال ---------- */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6 border">
          <Heading2 text={'روش‌های ارسال'}/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ToggleField
                title={'ارسال توسط تأمین کننده'}
                checked={productData.delivery_by_supplier}
                fieldKey="delivery_by_supplier"
                onChange={handleToggleChange}
            />
            <ToggleField
                title={'تحویل توسط مشتری'}
                checked={productData.delivery_by_customer}
                fieldKey="delivery_by_customer"
                onChange={handleToggleChange}
            />
            <ToggleField
                title={'ارسال توسط شرکت حمل'}
                checked={productData.delivery_by_carrier}
                fieldKey="delivery_by_carrier"
                onChange={handleToggleChange}
            />
          </div>
        </section>

        {/* ---------- گالری عکس ---------- */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6 border">
          <ProductImagesEdit
              productId={id}
              productData={productData}
              setProductData={setProductData}
              imageItems={imageItems}
              setImageItems={setImageItems}
          />
        </section>

        {/* ---------- توضیحات ---------- */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6 border">
          <ProductDescription
              productData={productData}
              setProductData={setProductData}
          />
        </section>
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6 border">
          <Heading2 text={'غیر فعال یا حذف کالا'}/>
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <ToggleField
                title={'فعال'}
                checked={productData.is_active}
                fieldKey="is_active"
                onChange={handleToggleChange}
            />

            <Button
                variant="danger"
                children={saving ? 'در حال حذف...' : 'حذف کالا'}
                onClick={handleDeleteProduct}
            />
          </div>
        </section>


        {/* ---------- دکمه‌های عملیات ---------- */}
        <div className="flex gap-3 flex-wrap">
          <Button
              variant={'primary'}
              children={saving ? 'در حال ارسال...' : 'ارسال برای تأیید'}
              onClick={handleSubmit}
          />
          <Button
              variant={'secondary'}
              children={saving ? 'در حال ذخیره...' : 'ذخیره پیش‌نویس'}
              onClick={handleSaveDraft}
          />

          <button
              onClick={() => setShowPreview(true)}
              className="px-6 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={saving}
          >
            پیش‌نمایش
          </button>
          <button
              onClick={handleCancel}
              className="px-6 py-2 rounded-md bg-gray-500 text-white hover:bg-gray-600 transition-colors"
          >
            انصراف
          </button>
        </div>
        {/* مودال پیش‌نمایش */}
        <PreviewModal
            open={showPreview}
            onClose={() => setShowPreview(false)}
        />
      </div>
  );
};

export default ProductEdit;




